import { useEffect, useState } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { config } from '@/config';
import { abandonSession } from '@/lib/sessionExpiry';
import { getActiveAccountId, setActiveAccountId } from '@/lib/activeAccount';
import { forgetAllPushAccounts, forgetPushAccount } from '@/lib/pushAccounts';
import type { HeldAccount } from '@/hooks/useHeldAccounts';
import { AuthShell } from './signup/AuthShell';

/**
 * Sign out, then land on the sign-in page.
 *
 * Two things here are deliberate and easy to undo by accident.
 *
 * **Local session state is stood down before the request goes out.** Clearing the cookie
 * kills the WebSocket, and `WebSocketContext` responds by calling `probeSession()`, which
 * gets a 401 from `/api/auth/current` and raises the re-auth dialog — over the top of the
 * sign-out page. It does that only while `authenticated()` is still true, and that reads
 * the cached `currentUser`. Invalidating the query is not enough: invalidation keeps the
 * cached value in place while it refetches. Setting it to `null` up front closes the
 * window, and `abandonSession()` clears any expiry flag already raised.
 *
 * **Only a 2xx or a 401 counts as signed out.** A 401 means there was no live session to
 * end, which is the outcome the user wanted. Any other failure means we do not know
 * whether the cookie was cleared — the removal cookie rides on a successful response — so
 * telling them they are signed out would be the more dangerous lie, particularly on a
 * shared computer. That path says so and offers a retry rather than claiming success.
 *
 * **It signs out this tab's account** (the request carries `X-Active-Account-Id`), or every
 * account the browser holds with `?all=1` (docs/MultiAccountSessions.md §5). With another
 * account still signed in, the tab carries on as the browser's first one; with none left, it
 * lands on sign-in as before.
 */
export default function SignOut() {
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const queryClient = useQueryClient();

  useEffect(() => {
    abandonSession();
    queryClient.setQueryData(['currentUser'], null);
    const everything = new URLSearchParams(window.location.search).get('all') === '1';
    // A bare instance so the app's response interceptors — which exist to turn a 401 into
    // a re-auth prompt — cannot act on the one request whose 401 is a success. Created per
    // call, so it carries the tab's account header from `axios.defaults`.
    const bare = axios.create();

    const done = async () => {
      // The backend has retired the signed-out accounts' push holds; their records here
      // only label notifications, and a signed-out account's no longer arrive. A request
      // naming no account signed out all of them.
      const account = getActiveAccountId();
      await (everything || account === null ? forgetAllPushAccounts() : forgetPushAccount(account));
      // Full navigation rather than a router push, matching the sign-out path in
      // `SessionExpiredDialog`: it drops every piece of cached state, which is what
      // signing out should do, and leaves no component holding data from the dead session.
      setActiveAccountId(null);
      const remaining = everything
        ? []
        : await bare
            .get<HeldAccount[]>(`${config.backendApi}/api/auth/accounts`, { withCredentials: true })
            .then(({ data }) => (Array.isArray(data) ? data.filter((a) => a.signedIn) : []), () => []);
      window.location.assign(remaining.length > 0 ? '/home' : '/signin?signedOut=1');
    };

    bare
      .post(`${config.backendApi}/api/auth/logout${everything ? '?scope=all' : ''}`, '', { withCredentials: true })
      .then(done, (error: unknown) => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          void done();
          return;
        }
        setFailed(true);
      });
  }, [queryClient, attempt]);

  if (failed) {
    return (
      <AuthShell title="Sign out failed">
        <div className="p-6 flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center text-destructive mb-4">
            <AlertCircle className="w-5 h-5" />
          </div>
          <p className="text-sm text-foreground">We couldn&rsquo;t sign you out.</p>
          <p className="text-xs text-muted-foreground mt-1.5">
            You may still be signed in on this device. Try again, and if you&rsquo;re on a shared computer,
            close the browser.
          </p>
          <Button className="w-full mt-4" onClick={() => {
              setFailed(false);
              setAttempt((n) => n + 1);
            }}>
            Try again
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Signing out">
      <div className="p-6 flex flex-col items-center text-center">
        <Loader2 className="w-6 h-6 text-muted-foreground animate-spin mb-4" />
        <p className="text-sm text-muted-foreground">Please wait…</p>
      </div>
    </AuthShell>
  );
}
